import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import wsPrintClient from '../../services/wsPrintClient';
import printerService from '../../services/printerService';

// Criar o contexto do Onboard
const OnboardContext = createContext();

const getInitialPrinterType = () => {
  try {
    const savedType = localStorage.getItem('printerType');
    if (savedType === 'websocket') return 'socket';
    if (savedType === 'bluetooth' || savedType === 'socket' || savedType === 'webbluetooth') {
      return savedType;
    }
  } catch (error) {
    void error;
  }
  return 'bluetooth';
};

// Provider do contexto Onboard
export const OnboardProvider = ({ children }) => {
  const { user, vehicleUsageToday, romaneioData } = useAuth();
  const [onboardData, setOnboardData] = useState({
    dtUso: '', // Data de uso atual
    userInfo: null,
    vehicleData: null,
    empresasData: [],
    totaisPorCliente: [],
    coletas: [],
    notasDoCliente: [], // Novo estado para armazenar as notas do cliente selecionado
    totals: {
      notas: 0,
      vols: 0,
      peso: 0,
      m3: 0,
      falta: 0,
      notasImpressas: 0,
      notasNaoImpressas: 0,
      etiquetasImpressas: 0,
      etiquetasNaoImpressas: 0,
      primeiraEtiqueta: null,
      ultimaEtiqueta: null
    }
  });
  // Nota selecionada externamente (contexto global)
  const [notaAtual, setNotaAtual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printerName, setPrinterName] = useState(() => {
    try {
      return localStorage.getItem('printerName') || null;
    } catch {
      return null;
    }
  });
  const [printerType, setPrinterType] = useState(getInitialPrinterType);
  const [printerDevice, setPrinterDevice] = useState(null);
  const [printerConnected, setPrinterConnected] = useState(() => {
    try {
      return printerService.isConnectedToPrinter();
    } catch {
      return false;
    }
  });
  const [printerEmulator, setPrinterEmulator] = useState(null);
  const [cnpjAtual, setCnpjAtual] = useState(null);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [veiculoDoDia, setVeiculoDoDia] = useState(null);
  const [modalPlacaVisible, setModalPlacaVisible] = useState(false);
  const [sswConfig, setSswConfig] = useState(null);
  const [buscaPath, setBuscaPath] = useState('database');
  // Evita que run() (auto-vínculo de uso do veículo) dispara duas vezes em
  // paralelo quando vehicleUsageToday popula durante o login por romaneio.
  const runEmAndamentoRef = useRef(false);
  const coletasErpCarregadasRef = useRef('');
  // Loading da busca de coletas do ERP (ssw0157): usado para exibir o PrinterLoader
  // na aba Coletas enquanto o backend roda a automação Playwright.
  const [coletasLoading, setColetasLoading] = useState(false);
  // Mensagem do ERP quando não há coletas (ex.: "Sem movimento de coletas...").
  const [coletasMensagem, setColetasMensagem] = useState('');

  useEffect(() => {
    let active = true;
    const fetchSswConfig = async () => {
      try {
        const cfg = await apiService.admin.getSswConfig();
        if (!active) return;
        setSswConfig(cfg);
        if (cfg?.buscaPath) {
          setBuscaPath(cfg.buscaPath);
        }
      } catch (err) {
        console.warn('Falha ao obter sswConfig no OnboardContext:', err);
      }
    };
    fetchSswConfig();
    return () => { active = false; };
  }, [user?.organizationId]);

  useEffect(() => {
    if (user) {
      printerService.setUser(user);
    }
  }, [user]);


  const mergeColetasComTotais = useCallback((erpColetas = [], totaisPorCliente = []) => {
    const totaisArr = Array.isArray(totaisPorCliente) ? totaisPorCliente : [];
    const erpArr = Array.isArray(erpColetas) ? erpColetas : [];

    const cleanCnpj = (val) => String(val || '').replace(/\D/g, '');

    const totaisMap = new Map();
    totaisArr.forEach((t) => {
      const key = cleanCnpj(t?.cdremetente);
      if (key) {
        totaisMap.set(key, t);
      }
    });

    const usedKeys = new Set();
    const merged = erpArr.map((c, index) => {
      const key = cleanCnpj(c?.cdremetente || c?.CdInscricao);
      const totalItem = key ? totaisMap.get(key) : null;
      if (key && totalItem) {
        usedKeys.add(key);
      }

      const remetenteNome = totalItem?.remetenteNome || c?.remetenteNome || c?.DsEntidade || 'Cliente não identificado';
      const cdremetente = c?.cdremetente || totalItem?.cdremetente || c?.CdInscricao || '';

      return {
        ...c,
        key: c?.key || `coleta-${c?.NrColeta || index}`,
        NrColeta: c?.NrColeta || String(index + 1),
        remetenteNome,
        DsEntidade: remetenteNome,
        cdremetente,
        CdInscricao: c?.CdInscricao || cdremetente,
        notasImpressas: Number(totalItem?.notasImpressas || 0),
        notasNaoImpressas: Number(totalItem?.notasNaoImpressas || 0),
        volumesImpressosTotal: Number(totalItem?.volumesImpressosTotal || 0),
        volumesNaoImpressosTotal: Number(totalItem?.volumesNaoImpressosTotal || 0),
        volumesTotal: Number(totalItem?.volumesTotal || 0),
        pesoImpressoTotal: totalItem?.pesoImpressoTotal != null ? totalItem.pesoImpressoTotal : 0,
        pesoNaoImpressoTotal: totalItem?.pesoNaoImpressoTotal != null ? totalItem.pesoNaoImpressoTotal : 0,
        pesoTotal: totalItem?.pesoTotal != null ? totalItem.pesoTotal : 0,
        m3ImpressoTotal: totalItem?.m3ImpressoTotal != null ? totalItem.m3ImpressoTotal : 0,
        m3NaoImpressoTotal: totalItem?.m3NaoImpressoTotal != null ? totalItem.m3NaoImpressoTotal : 0,
        m3Total: totalItem?.m3Total != null ? totalItem.m3Total : 0,
        notas: totalItem?.notas || c?.notas || [],
      };
    });

    totaisArr.forEach((t, index) => {
      const key = cleanCnpj(t?.cdremetente);
      if (!key || !usedKeys.has(key)) {
        const nome = t?.remetenteNome || 'Cliente não identificado';
        merged.push({
          key: `totais-${t?.cdremetente || index}`,
          NrColeta: String(merged.length + 1),
          remetenteNome: nome,
          DsEntidade: nome,
          cdremetente: t?.cdremetente || '',
          CdInscricao: t?.cdremetente || '',
          notasImpressas: Number(t?.notasImpressas || 0),
          notasNaoImpressas: Number(t?.notasNaoImpressas || 0),
          volumesImpressosTotal: Number(t?.volumesImpressosTotal || 0),
          volumesNaoImpressosTotal: Number(t?.volumesNaoImpressosTotal || 0),
          volumesTotal: Number(t?.volumesTotal || 0),
          pesoImpressoTotal: t?.pesoImpressoTotal != null ? t.pesoImpressoTotal : 0,
          pesoNaoImpressoTotal: t?.pesoNaoImpressoTotal != null ? t.pesoNaoImpressoTotal : 0,
          pesoTotal: t?.pesoTotal != null ? t.pesoTotal : 0,
          m3ImpressoTotal: t?.m3ImpressoTotal != null ? t.m3ImpressoTotal : 0,
          m3NaoImpressoTotal: t?.m3NaoImpressoTotal != null ? t.m3NaoImpressoTotal : 0,
          m3Total: t?.m3Total != null ? t.m3Total : 0,
          notas: t?.notas || [],
        });
      }
    });

    return merged;
  }, []);

  const buscarNotasTotais = useCallback(async (veiculoId, dtUso, usuarioId) => {
    try {
      const placaFallback = (veiculoDoDia?.veiculo?.placa || veiculoDoDia?.placa || onboardData?.vehicleData?.placa || '').toUpperCase();
      if (!veiculoId || !dtUso || !usuarioId) return;
      // Buscar apenas totais agregados por cliente
      try {
        const totais = await apiService.notaVeiculoUso.findTotalsPorVeiculo({ veiculoId, dia: dtUso, usuarioId });

        const arr = Array.isArray(totais) ? totais : [];
        const sumNotas = arr.reduce((s, it) => s + Number(it?.notasNaoImpressas || 0) + Number(it?.notasImpressas || 0), 0);
        const sumPeso = arr.reduce((s, it) => s + (typeof it?.pesoTotal === 'string' ? parseFloat(it.pesoTotal) : Number(it?.pesoTotal || 0)), 0);
        const sumM3 = arr.reduce((s, it) => s + (typeof it?.m3Total === 'string' ? parseFloat(it.m3Total) : Number(it?.m3Total || 0)), 0);
        const sumVols = arr.reduce((s, it) => s + (typeof it?.volumesTotal === 'string' ? parseFloat(it.volumesTotal) : Number(it?.volumesTotal || 0)), 0);
        const sumVolsNaoImp = arr.reduce((s, it) => s + (typeof it?.volumesNaoImpressosTotal === 'string' ? parseFloat(it.volumesNaoImpressosTotal) : Number(it?.volumesNaoImpressosTotal || 0)), 0);

        const sumNotasImp = arr.reduce((s, it) => s + Number(it?.notasImpressas || 0), 0);
        const sumNotasNaoImp = arr.reduce((s, it) => s + Number(it?.notasNaoImpressas || 0), 0);
        const sumEtiquetasImp = arr.reduce((s, it) => s + (Number(it?.volumesTotal || 0) - Number(it?.volumesNaoImpressosTotal || 0)), 0);
        const sumEtiquetasNaoImp = arr.reduce((s, it) => s + Number(it?.volumesNaoImpressosTotal || 0), 0);

        // Buscar notas individuais para pegar as datas da primeira e última etiqueta
        let minData = null;
        let maxData = null;
        try {
          const notasIndividuais = await apiService.notaVeiculoUso.list({ veiculoId, dia: dtUso, usuarioId });

          if (Array.isArray(notasIndividuais)) {
            // Criar um mapa de notas por remetente para popular as coletas
            const cleanCnpj = (val) => String(val || '').replace(/\D/g, '');
            const notasPorRemetente = {};

            notasIndividuais.forEach(n => {
              const baseNota = n.nota || {};
              // Normalizar cidade e UF se estiverem faltando no destinatário
              if (baseNota.destinatario) {
                if (!baseNota.destinatario.cidade && baseNota.cidade) {
                  baseNota.destinatario.cidade = baseNota.cidade;
                }
                if (!baseNota.destinatario.uf && baseNota.uf) {
                  baseNota.destinatario.uf = baseNota.uf;
                }
              }

              const rawCnpj = baseNota.cdremetente || n.cdremetente;
              const key = cleanCnpj(rawCnpj);
              if (key) {
                if (!notasPorRemetente[key]) {
                  notasPorRemetente[key] = [];
                }

                // Usar createdAt como primeira_etiqueta e ultima_etiqueta do backend
                const notaComDatas = {
                  ...n,
                  nota: baseNota,
                  primeira_etiqueta: n.createdAt,
                  ultima_etiqueta: n.ultima_etiqueta
                };

                notasPorRemetente[key].push(notaComDatas);
              }

              if (n.status === 1) { // Apenas impressas têm datas relevantes de etiqueta
                // Primeira etiqueta (createdAt)
                if (n.createdAt) {
                  const d = n.createdAt;
                  if (!minData || String(d) < String(minData)) minData = d;
                }
                // Última etiqueta (campo ultima_etiqueta vindo do backend)
                if (n.ultima_etiqueta) {
                  const d = n.ultima_etiqueta;
                  if (!maxData || String(d) > String(maxData)) maxData = d;
                }
              }
            });

            // Mesclar as notas em cada item de totaisPorCliente
            arr.forEach(item => {
              const key = cleanCnpj(item.cdremetente);
              if (key && notasPorRemetente[key]) {
                item.notas = notasPorRemetente[key];
              } else {
                item.notas = [];
              }
            });
          }
        } catch (errDate) {
          console.error("Erro ao buscar datas de etiquetas:", errDate);
        }

        setOnboardData(prev => ({
          ...prev,
          totaisPorCliente: arr,
          coletas: mergeColetasComTotais(prev.coletas, arr),
          totals: {
            ...(prev.totals || {}),
            notas: Number(sumNotas || 0),
            vols: Number(sumVols || 0),
            peso: Number(Number(sumPeso || 0).toFixed(2)),
            m3: Number(Number(sumM3 || 0).toFixed(2)),
            falta: Math.round(Number(sumVolsNaoImp || 0)),
            notasImpressas: sumNotasImp,
            notasNaoImpressas: sumNotasNaoImp,
            etiquetasImpressas: sumEtiquetasImp,
            etiquetasNaoImpressas: sumEtiquetasNaoImp,
            primeiraEtiqueta: minData,
            ultimaEtiqueta: maxData
          },
        }));
      } catch (error) {
        void error;
      }

      // Fallback: se nada veio com veiculoId/usuarioId, tentar por placa+dia
      try {
        const atual = onboardData?.totaisPorCliente || [];
        if ((!Array.isArray(atual) || atual.length === 0) && placaFallback) {
          const totaisViaPlaca = await apiService.notaVeiculoUso.findTotalsPorVeiculo({ placa: placaFallback, dia: dtUso });
          const arrPlaca = Array.isArray(totaisViaPlaca) ? totaisViaPlaca : [];
          const sumNotasP = arrPlaca.reduce((s, it) => s + Number(it?.notasNaoImpressas || 0) + Number(it?.notasImpressas || 0), 0);
          const sumPesoP = arrPlaca.reduce((s, it) => s + (typeof it?.pesoTotal === 'string' ? parseFloat(it.pesoTotal) : Number(it?.pesoTotal || 0)), 0);
          const sumM3P = arrPlaca.reduce((s, it) => s + (typeof it?.m3Total === 'string' ? parseFloat(it.m3Total) : Number(it?.m3Total || 0)), 0);
          const sumVolsP = arrPlaca.reduce((s, it) => s + (typeof it?.volumesTotal === 'string' ? parseFloat(it.volumesTotal) : Number(it?.volumesTotal || 0)), 0);
          const sumVolsNaoImpP = arrPlaca.reduce((s, it) => s + (typeof it?.volumesNaoImpressosTotal === 'string' ? parseFloat(it.volumesNaoImpressosTotal) : Number(it?.volumesNaoImpressosTotal || 0)), 0);

          const sumNotasImpP = arrPlaca.reduce((s, it) => s + Number(it?.notasImpressas || 0), 0);
          const sumNotasNaoImpP = arrPlaca.reduce((s, it) => s + Number(it?.notasNaoImpressas || 0), 0);
          const sumEtiquetasImpP = arrPlaca.reduce((s, it) => s + (Number(it?.volumesTotal || 0) - Number(it?.volumesNaoImpressosTotal || 0)), 0);
          const sumEtiquetasNaoImpP = arrPlaca.reduce((s, it) => s + Number(it?.volumesNaoImpressosTotal || 0), 0);

          // Buscar notas individuais para pegar as datas da primeira e última etiqueta (fallback)
          let minDataP = null;
          let maxDataP = null;
          try {
            const notasIndividuaisP = await apiService.notaVeiculoUso.list({ placa: placaFallback, dia: dtUso });
            if (Array.isArray(notasIndividuaisP)) {
              // Criar um mapa de notas por remetente para popular as coletas (fallback)
              const cleanCnpj = (val) => String(val || '').replace(/\D/g, '');
              const notasPorRemetenteP = {};

              notasIndividuaisP.forEach(n => {
                const baseNota = n.nota || {};
                // Normalizar cidade e UF se estiverem faltando no destinatário
                if (baseNota.destinatario) {
                  if (!baseNota.destinatario.cidade && baseNota.cidade) {
                    baseNota.destinatario.cidade = baseNota.cidade;
                  }
                  if (!baseNota.destinatario.uf && baseNota.uf) {
                    baseNota.destinatario.uf = baseNota.uf;
                  }
                }

                const rawCnpj = baseNota.cdremetente || n.cdremetente;
                const key = cleanCnpj(rawCnpj);
                if (key) {
                  if (!notasPorRemetenteP[key]) {
                    notasPorRemetenteP[key] = [];
                  }

                  // Usar createdAt como primeira_etiqueta e ultima_etiqueta do backend
                  const notaComDatas = {
                    ...n,
                    nota: baseNota,
                    primeira_etiqueta: n.createdAt,
                    ultima_etiqueta: n.ultima_etiqueta
                  };

                  notasPorRemetenteP[key].push(notaComDatas);
                }

                if (n.status === 1) {
                  // Primeira etiqueta (createdAt)
                  if (n.createdAt) {
                    const d = n.createdAt;
                    if (!minDataP || String(d) < String(minDataP)) minDataP = d;
                  }
                  // Última etiqueta (campo ultima_etiqueta vindo do backend)
                  if (n.ultima_etiqueta) {
                    const d = n.ultima_etiqueta;
                    if (!maxDataP || String(d) > String(maxDataP)) maxDataP = d;
                  }
                }
              });

              // Mesclar as notas em cada item de arrPlaca
              arrPlaca.forEach(item => {
                const key = cleanCnpj(item.cdremetente);
                if (key && notasPorRemetenteP[key]) {
                  item.notas = notasPorRemetenteP[key];
                } else {
                  item.notas = [];
                }
              });
            }
          } catch (error) {
            void error;
          }

          setOnboardData(prev => ({
            ...prev,
            totaisPorCliente: arrPlaca,
            coletas: mergeColetasComTotais(prev.coletas, arrPlaca),
            totals: {
              ...(prev.totals || {}),
              notas: Number(sumNotasP || 0),
              vols: Number(sumVolsP || 0),
              peso: Number(Number(sumPesoP || 0).toFixed(2)),
              m3: Number(Number(sumM3P || 0).toFixed(2)),
              falta: Math.round(Number(sumVolsNaoImpP || 0)),
              notasImpressas: sumNotasImpP,
              notasNaoImpressas: sumNotasNaoImpP,
              etiquetasImpressas: sumEtiquetasImpP,
              etiquetasNaoImpressas: sumEtiquetasNaoImpP,
              primeiraEtiqueta: minDataP,
              ultimaEtiqueta: maxDataP
            },
          }));
        }
      } catch (error) {
        void error;
      }

    } catch (error) {
      void error;
    }
  }, [onboardData?.vehicleData?.capacidadeIdeal]);

  // Atualiza automaticamente o array de dados de coletas (coleta-header) sempre que houver impressão ou reimpressão de etiquetas
  useEffect(() => {
    const handleEtiquetasAlteradas = async () => {
      try {
        const vehicleId = veiculoDoDia?.veiculo?.id || veiculoDoDia?.veiculoId || onboardData?.vehicleData?.id;
        const hoje = new Date();
        const y = hoje.getFullYear();
        const m = String(hoje.getMonth() + 1).padStart(2, '0');
        const d = String(hoje.getDate()).padStart(2, '0');
        const diaHoje = `${y}-${m}-${d}`;
        const dtUso = (onboardData?.dtUso ? String(onboardData.dtUso).substring(0, 10) : '') || diaHoje;
        const usuarioId = user?.id;
        if (vehicleId && dtUso && usuarioId) {
          await buscarNotasTotais(vehicleId, dtUso, usuarioId);
        }
      } catch (err) {
        console.error('Erro ao atualizar dados de coleta-header após evento de etiquetas:', err);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('etiquetasImpressas', handleEtiquetasAlteradas);
      window.addEventListener('etiquetasReimpressas', handleEtiquetasAlteradas);
      return () => {
        window.removeEventListener('etiquetasImpressas', handleEtiquetasAlteradas);
        window.removeEventListener('etiquetasReimpressas', handleEtiquetasAlteradas);
      };
    }
  }, [veiculoDoDia, onboardData?.dtUso, onboardData?.vehicleData?.id, user?.id, buscarNotasTotais]);

  // Coletas vindas do ERP popula APENAS identificação (número, nome, CNPJ) e
  // informações descritivas. Volumes/Peso/M3 e notas impressas/não impressas
  // são dados do banco local (nota_veiculo_usos/etiquetas) — NÃO devem ser
  // preenchidos por valores do relatório do ERP.
  const mapColetaErpParaOnboard = useCallback((coleta, index) => ({
    key: `${coleta?.unidade || ''}-${coleta?.numeroColeta || index}`,
    NrColeta: coleta?.numeroColeta || String(index + 1),
    unidade: coleta?.unidade || '',
    tipoColeta: coleta?.tipoColeta || '',
    DsEntidade: coleta?.remetente?.nome || 'Cliente não identificado',
    remetenteNome: coleta?.remetente?.nome || 'Cliente não identificado',
    CdInscricao: coleta?.remetente?.cnpj || '',
    cdremetente: coleta?.remetente?.cnpj || '',
    volumesTotal: 0,
    volumesNaoImpressosTotal: 0,
    volumesImpressosTotal: 0,
    pesoTotal: 0,
    pesoNaoImpressoTotal: 0,
    pesoImpressoTotal: 0,
    m3Total: 0,
    m3NaoImpressoTotal: 0,
    m3ImpressoTotal: 0,
    notasImpressas: 0,
    notasNaoImpressas: 0,
    notas: [],
    dataHoraLimite: coleta?.dataHoraLimite || '',
    situacaoAtual: coleta?.situacaoAtual || '',
    motorista: coleta?.motorista || '',
    veiculo: coleta?.veiculo || '',
    endereco: coleta?.remetente?.endereco || '',
    cidade: coleta?.remetente?.cidade || '',
    setor: coleta?.remetente?.setor || '',
  }), []);

  const buscarColetasErp = useCallback(async () => {
    if (!user?.romaneio) return false;

    const placa = String(
      veiculoDoDia?.veiculo?.placa ||
      veiculoDoDia?.placa ||
      onboardData?.vehicleData?.placa ||
      romaneioData?.placa ||
      user?.codigo ||
      '',
    )
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 7);
    const unidade = String(
      user?.assignedBranch?.sigla || romaneioData?.unidade || user?.empresa || '',
    )
      .toUpperCase()
      .trim();

    let dominio = '';
    try {
      const erpSession = JSON.parse(localStorage.getItem('erpSession') || 'null');
      const loginRom = JSON.parse(
        localStorage.getItem('loginRomaneioData_sw') ||
        localStorage.getItem('romaneioData_sw') ||
        localStorage.getItem('loginRomaneioData') ||
        'null'
      );
      dominio = String(erpSession?.dominio || loginRom?.dominio || '').trim();
    } catch (error) {
      void error;
    }

    if (!placa || !unidade || !dominio) return false;

    const requestKey = `${dominio}|${unidade}|${placa}`;
    if (coletasErpCarregadasRef.current === requestKey) return true;

    setColetasLoading(true);
    try {
      const response = await apiService.coletas.buscarPorVeiculo({
        dominio,
        unidade,
        placa,
      });
      if (!response?.ok) {
        return false;
      }

      const mapped = Array.isArray(response?.coletas)
        ? response.coletas.map(mapColetaErpParaOnboard)
        : [];

      setColetasMensagem(String(response?.mensagem || '').trim());
      setOnboardData((prev) => ({
        ...prev,
        coletas: mergeColetasComTotais(mapped, prev.totaisPorCliente),
      }));
      coletasErpCarregadasRef.current = requestKey;
      return true;
    } catch (error) {
      console.warn('Falha ao buscar coletas do ERP (ssw0157):', error?.message || error);
      return false;
    } finally {
      setColetasLoading(false);
    }
  }, [
    user?.romaneio,
    user?.assignedBranch?.sigla,
    user?.empresa,
    user?.codigo,
    veiculoDoDia?.veiculo?.placa,
    veiculoDoDia?.placa,
    onboardData?.vehicleData?.placa,
    romaneioData?.placa,
    romaneioData?.unidade,
    mapColetaErpParaOnboard,
  ]);




  // Verificar se o usuário tem veículo vinculado (uso em aberto ou do dia atual)
  useEffect(() => {
    const toLocalYYYYMMDD = (date) => {
      try {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      } catch (error) {
        void error;
        return '';
      }
    };

    const formatDateToYYYYMMDD = (dateString) => {
      if (!dateString) return '';
      try {
        return new Date(dateString).toISOString().split('T')[0];
      } catch (e) {
        console.error("Erro ao formatar data:", e);
        return '';
      }
    };

    const run = async () => {
      if (!user?.codigo) return;
      if (runEmAndamentoRef.current) return;
      runEmAndamentoRef.current = true;
      try {
        let currentBuscaPath = buscaPath;
        try {
          const cfg = await apiService.admin.getSswConfig();
          if (cfg?.buscaPath) {
            currentBuscaPath = cfg.buscaPath;
            setBuscaPath(cfg.buscaPath);
            setSswConfig(cfg);
          }
        } catch { }

        // Se AuthContext já trouxe o uso do dia, usar diretamente
        if (vehicleUsageToday) {
          const placaAuth = vehicleUsageToday?.veiculo?.placa || vehicleUsageToday?.pliculo || '';
          setVeiculoDoDia(vehicleUsageToday);
          setOnboardData(prev => ({
            ...prev,
            vehicleData: {
              ...(prev.vehicleData || {}),
              id: vehicleUsageToday?.veiculo?.id || vehicleUsageToday?.veiculoId,
              placa: placaAuth,
            },
            dtUso: formatDateToYYYYMMDD(vehicleUsageToday.dtInicio || vehicleUsageToday.dtUso || '')
          }));
          setModalPlacaVisible(false);
          setLoading(false);

          return;
        }

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const diaLocal = toLocalYYYYMMDD(hoje);

        // Se o login foi por romaneio (ou usuário tem placa no código/romaneioData),
        // o veículo já foi selecionado no login e DEVE ser vinculado automaticamente.
        const rawRomPlaca = romaneioData?.placa || user?.codigo || user?.usuario || '';
        const placaRom = String(rawRomPlaca)
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, '')
          .slice(0, 7);

        if (user?.romaneio || (placaRom.length === 7 && /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/i.test(placaRom))) {
          const filialRom = String(romaneioData?.unidade || user?.empresa || user?.assignedBranch?.sigla || '').toUpperCase().trim();
          let uso = null;

          // 1. Busca se já existe uso aberto para esta placa
          try {
            const listP = await apiService.veiculoUso.list({ placa: placaRom });
            const arrP = Array.isArray(listP?.data) ? listP.data : Array.isArray(listP?.items) ? listP.items : Array.isArray(listP) ? listP : [];
            uso = arrP.find((u) => !u.dtFim) || arrP.find((u) => {
              try {
                const dt = String(u.dtUso || u.dtInicio || '').substring(0, 10);
                return dt === diaLocal;
              } catch { return false; }
            }) || arrP[0] || null;
          } catch { }

          // 2. Busca por usuarioCodigo se não encontrou por placa
          if (!uso && user?.codigo) {
            try {
              const listU = await apiService.veiculoUso.list({ usuarioCodigo: String(user.codigo) });
              const arrU = Array.isArray(listU?.data) ? listU.data : Array.isArray(listU?.items) ? listU.items : Array.isArray(listU) ? listU : [];
              uso = arrU.find((u) => !u.dtFim) || arrU.find((u) => {
                try {
                  const dt = String(u.dtUso || u.dtInicio || '').substring(0, 10);
                  return dt === diaLocal;
                } catch { return false; }
              }) || arrU[0] || null;
            } catch { }
          }

          // 3. Busca o veículo no banco para pegar dados completos (id, peso, m3, filial)
          let v = null;
          try {
            v = await apiService.veiculo.getByPlaca(placaRom);
            if (v && typeof v?.message === 'string' && v.message.toLowerCase().includes('não encontrado')) {
              v = null;
            }
          } catch { }

          // 4. Se não tem uso ainda, tenta criar no backend
          if (!uso && v) {
            const filialSigla = v?.branch?.sigla || filialRom || 'JGA';
            try {
              uso = await apiService.veiculoUso.create({
                placa: placaRom,
                dia: diaLocal,
                usuarioCodigo: String(user?.codigo || placaRom),
                filial: filialSigla,
              });
            } catch (createErr) {
              console.warn('Auto-vínculo de uso do veículo falhou:', createErr?.message);
            }
          }

          // 5. Se ainda não há registro persistido de uso, monta o uso do dia com os dados do veículo
          if (!uso) {
            uso = {
              id: null,
              placa: placaRom,
              veiculoId: v?.id || null,
              veiculo: v || { id: null, placa: placaRom },
              dtUso: diaLocal,
              dtInicio: new Date(),
              dtFim: null,
            };
          }

          const placaUso = uso?.veiculo?.placa || uso?.placa || placaRom;
          setVeiculoDoDia(uso);
          setOnboardData((prev) => ({
            ...prev,
            vehicleData: { ...(prev.vehicleData || {}), placa: placaUso, ...(v || {}) },
            dtUso: formatDateToYYYYMMDD(uso.dtInicio || uso.dtUso || '') || diaLocal,
          }));
          setModalPlacaVisible(false);
          setLoading(false);
          return;
        }

        // Fluxo normal para usuários administrativos/desktop que não logaram por romaneio
        const usosUsuario = await apiService.veiculoUso.list({ usuarioCodigo: String(user.codigo) });
        let usoAtual = null;
        if (Array.isArray(usosUsuario) && usosUsuario.length > 0) {
          usoAtual = usosUsuario.find((u) => !u.dtFim) || usosUsuario.find((u) => {
            try {
              const dtStr = String(u.dtUso || u.dtInicio || '').substring(0, 10);
              return dtStr === diaLocal;
            } catch {
              return false;
            }
          }) || null;
        }

        if (!usoAtual) {
          const usosDoDia = await apiService.veiculoUso.list({ usuarioCodigo: String(user.codigo), dia: diaLocal });
          if (Array.isArray(usosDoDia) && usosDoDia.length > 0) {
            usoAtual = usosDoDia[0];
          }
        }

        if (usoAtual) {
          const placa = usoAtual?.veiculo?.placa || usoAtual?.placa || '';
          setVeiculoDoDia(usoAtual);
          const veiculoIdVal = usoAtual?.veiculo?.id || usoAtual?.veiculoId || null;
          setOnboardData(prev => ({
            ...prev,
            vehicleData: { ...(prev.vehicleData || {}), placa, id: veiculoIdVal },
            dtUso: formatDateToYYYYMMDD(usoAtual.dtInicio || usoAtual.dtUso || '')
          }));
          setModalPlacaVisible(false);
          setLoading(false);
        } else {
          setVeiculoDoDia(null);
          if (currentBuscaPath === 'ssw') {
            setModalPlacaVisible(false);
            setLoading(false);
            return;
          }
          setModalPlacaVisible(true);
          setLoading(false);
        }
      } catch (error) {
        void error;
        if (buscaPath !== 'ssw') {
          setModalPlacaVisible(true);
        } else {
          setModalPlacaVisible(false);
        }
        setLoading(false);
      } finally {
        runEmAndamentoRef.current = false;
      }
    };
    run();
  }, [user?.codigo, user?.romaneio, romaneioData?.placa, vehicleUsageToday, buscaPath]);

  useEffect(() => {
    if (!user?.romaneio) return;
    if (loading) return;
    void buscarColetasErp();
  }, [user?.romaneio, loading, buscarColetasErp]);

  // Callbacks globais para WS-Print para manter printerConnected reativo em todo o contexto
  useEffect(() => {
    wsPrintClient.setCallbacks({
      onOpen: ({ url }) => {
        void url;
        try { setPrinterConnected(false); } catch (error) { void error; }
        try { if (!printerEmulator) setPrinterEmulator('WebSocket'); } catch (error) { void error; }
      },
      onClose: () => {
        try {
          setPrinterConnected(false);
          setPrinterName(null);
          setPrinterDevice(null);
          setPrinterEmulator(null);
        } catch (error) { void error; }
      },
      onUnpaired: () => {
        try {
          setPrinterConnected(false);
        } catch (error) { void error; }
      },
      onPrinter: (nameOrId) => {
        try {
          const display = typeof nameOrId === 'string' ? nameOrId : String(nameOrId || '');
          setPrinterName(display);
          if (display && display.trim() !== '' && wsPrintClient.isConnected) {
            setPrinterConnected(true);
          } else {
            setPrinterConnected(false);
          }
        } catch (error) { void error; }
      },
      onStatus: (st) => {
        try {
          if (Object.prototype.hasOwnProperty.call(st || {}, 'connected') && st.connected === false) {
            setPrinterConnected(false);
          }
          if (Object.prototype.hasOwnProperty.call(st || {}, 'paired') && st.paired === false) {
            setPrinterConnected(false);
          }
          if (Object.prototype.hasOwnProperty.call(st || {}, 'pairedWith')) {
            if (st.pairedWith) {
              setPrinterDevice(String(st.pairedWith));
              if (!printerEmulator) setPrinterEmulator('WebSocket');
            } else {
              setPrinterConnected(false);
            }
          }
          if (Object.prototype.hasOwnProperty.call(st || {}, 'uuid')) {
            if (st.uuid) setPrinterDevice(String(st.uuid));
          }
        } catch (error) { void error; }
      }
    });
    // Não há cleanup necessário além de fechar o socket em fluxos específicos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      window.setarImpressora = (deviceName) => {
        const raw = typeof deviceName === 'string' ? deviceName.trim() : '';
        let normalizedName = raw;
        if (raw && ((raw.startsWith('{') && raw.endsWith('}')) || (raw.startsWith('[') && raw.endsWith(']')))) {
          try {
            const parsed = JSON.parse(raw);
            normalizedName = String(
              parsed?.deviceName ??
              parsed?.printerName ??
              parsed?.nome ??
              parsed?.name ??
              parsed?.device?.name ??
              parsed?.device?.nome ??
              raw
            ).trim();
          } catch (error) {
            void error;
          }
        }
        normalizedName = normalizedName.replace(/^bluetooth[\s:|-]*/i, '').trim();
        if (!normalizedName || ['bluetooth', 'websocket', 'socket'].includes(normalizedName.toLowerCase())) return;

        try { setPrinterConnected(true); } catch (error) { void error; }
        try { setPrinterName(normalizedName); } catch (error) { void error; }
        try { setPrinterEmulator('Bluetooth'); } catch (error) { void error; }
        try { setPrinterDevice(normalizedName); } catch (error) { void error; }
        try {
          printerService.syncState({ connected: true, name: normalizedName, type: 'bluetooth', device: { nome: normalizedName } });
          if (printerService && printerService.callbacks && typeof printerService.callbacks.onConnect === 'function') {
            printerService.callbacks.onConnect({ type: 'bluetooth', device: normalizedName, status: 'connected' });
          }
        } catch (error) { void error; }
      };
    } catch (error) { void error; }
    return () => { try { delete window.setarImpressora; } catch (error) { void error; } };
  }, []);

  useEffect(() => {
    try {
      printerService.syncState({
        connected: printerConnected,
        name: printerName,
        type: printerType,
        device: printerDevice ? { uuid: printerDevice, nome: printerName } : null,
      });
    } catch (error) { void error; }
  }, [printerConnected, printerName, printerType, printerDevice]);

  useEffect(() => {
    try {
      if (printerType) {
        localStorage.setItem('printerType', printerType);
      } else {
        localStorage.removeItem('printerType');
      }
    } catch (error) {
      void error;
    }
  }, [printerType]);

  useEffect(() => {
    try {
      if (printerName) {
        localStorage.setItem('printerName', printerName);
      }
    } catch (error) {
      void error;
    }
  }, [printerName]);

  // Función para actualizar datos específicos
  const updateOnboardData = (newData) => {
    setOnboardData(prevData => ({
      ...prevData,
      ...newData
    }));
  };

  // Seleciona cliente/coleta atual para foco na aba de notas
  const selecionarCliente = (coleta) => {
    if (!coleta) {
      setClienteSelecionado(null);
      setCnpjAtual(null);
      return;
    }
    setClienteSelecionado(coleta);
    setCnpjAtual(coleta.CdInscricao || null);
  };

  // Função para atualizar totais
  const updateTotals = (newTotals) => {
    setOnboardData(prevData => ({
      ...prevData,
      totals: {
        ...prevData.totals,
        ...newTotals
      }
    }));
  };

  // Função para adicionar/atualizar empresa
  const updateEmpresaData = (empresaKey, newData) => {
    setOnboardData(prevData => ({
      ...prevData,
      empresasData: prevData.empresasData.map(empresa =>
        empresa.key === empresaKey
          ? { ...empresa, ...newData }
          : empresa
      )
    }));
  };

  const contextValue = {
    onboardData,
    setOnboardData,
    updateOnboardData,
    updateTotals,
    updateEmpresaData,
    buscarNotasTotais,
    buscarColetasErp,
    coletasLoading,
    coletasMensagem,
    loading,
    error,
    // Dados específicos para fácil acesso
    userInfo: onboardData.userInfo,
    vehicleData: onboardData.vehicleData,
    empresasData: onboardData.empresasData,
    totals: onboardData.totals,
    coletas: onboardData.coletas,
    cnpjAtual,
    clienteSelecionado,
    selecionarCliente,
    // Nota selecionada externamente
    notaAtual,
    setNotaAtual,
    // Printer data
    printerName,
    setPrinterName,
    printerType,
    setPrinterType,
    printerDevice,
    setPrinterDevice,
    printerConnected,
    setPrinterConnected,
    printerEmulator,
    setPrinterEmulator
    ,
    // Veículo do dia e modal de placa
    veiculoDoDia,
    setVeiculoDoDia,
    modalPlacaVisible,
    setModalPlacaVisible,
    // SSW Configuração de busca (database vs ssw)
    sswConfig,
    setSswConfig,
    buscaPath,
    setBuscaPath,
  };

  return (
    <OnboardContext.Provider value={contextValue}>
      {children}
    </OnboardContext.Provider>
  );
};

// Hook personalizado para usar o contexto Onboard
// eslint-disable-next-line react-refresh/only-export-components
export const useOnboard = () => {
  const context = useContext(OnboardContext);
  if (context) return context;
  return {
    onboardData: {
      dtUso: '',
      userInfo: null,
      vehicleData: null,
      empresasData: [],
      totaisPorCliente: [],
      notasDoCliente: [],
      totals: {
        notas: 0,
        vols: 0,
        peso: 0,
        m3: 0,
        falta: 0,
        notasImpressas: 0,
        notasNaoImpressas: 0,
        etiquetasImpressas: 0,
        etiquetasNaoImpressas: 0,
        primeiraEtiqueta: null,
        ultimaEtiqueta: null
      },
      coletas: [],
    },
    setOnboardData: () => { },
    updateOnboardData: () => { },
    updateTotals: () => { },
    updateEmpresaData: () => { },
    buscarNotasTotais: async () => { },
    buscarColetasErp: async () => false,
    coletasLoading: false,
    coletasMensagem: '',
    loading: true,
    error: null,
    userInfo: null,
    vehicleData: null,
    empresasData: [],
    totals: {
      notas: 0,
      vols: 0,
      peso: 0,
      m3: 0,
      falta: 0,
      notasImpressas: 0,
      notasNaoImpressas: 0,
      etiquetasImpressas: 0,
      etiquetasNaoImpressas: 0,
      primeiraEtiqueta: null,
      ultimaEtiqueta: null
    },
    coletas: [],
    cnpjAtual: null,
    clienteSelecionado: null,
    selecionarCliente: () => { },
    notaAtual: null,
    setNotaAtual: () => { },
    printerName: null,
    setPrinterName: () => { },
    printerType: getInitialPrinterType(),
    setPrinterType: () => { },
    printerDevice: null,
    setPrinterDevice: () => { },
    printerConnected: false,
    setPrinterConnected: () => { },
    printerEmulator: null,
    setPrinterEmulator: () => { },
    veiculoDoDia: null,
    setVeiculoDoDia: () => { },
    modalPlacaVisible: false,
    setModalPlacaVisible: () => { },
    sswConfig: null,
    setSswConfig: () => { },
    buscaPath: 'database',
    setBuscaPath: () => { },
  };
};

export default OnboardContext;
